import React, { useState, useEffect, useMemo } from 'react';
import { Text, Flex, ActionButton, SearchField, RangeSlider, Button, View } from '@adobe/react-spectrum';
import './FilterPanel.css';
import { ManaSelector } from './ManaSelector';
import Filter from '@spectrum-icons/workflow/Filter';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';

const POWER_PRESETS = [
    { label: 'Casual', low: 1, high: 4 },
    { label: 'Focused', low: 5, high: 7 },
    { label: 'High', low: 8, high: 10 },
    { label: 'Any', low: 1, high: 10 },
];

const QUERY_TYPES = [
    { value: 'commander', label: 'Commander' },
    { value: 'author', label: 'Author' },
    { value: 'title', label: 'Title' },
];

const DEFAULT_FILTERS = {
    query: '',
    queryType: 'commander',
    colorFilters: '',
    range: { high: 10, low: 1 },
};

function countActiveFilters(filters) {
    if (!filters) return 0;
    let count = 0;
    if (filters.query) count += 1;
    if (filters.colorFilters) count += 1;
    const low = filters?.range?.low ?? 1;
    const high = filters?.range?.high ?? 10;
    if (low > 1 || high < 10) count += 1;
    return count;
}

export function FilterPanelTrigger({ filters, isOpen, onToggle }) {
    const activeCount = useMemo(() => countActiveFilters(filters), [filters]);
    return (
        <ActionButton
            marginStart={10}
            UNSAFE_className={`resultsToolbarButton filterTriggerButton ${isOpen ? 'is-expanded' : ''}`}
            onPress={onToggle}
            aria-expanded={isOpen}
            aria-label="Toggle filters"
        >
            Filters
            <Filter />
            {activeCount > 0 && (
                <span className="filterCountBadge" aria-label={`${activeCount} active filters`}>
                    {activeCount}
                </span>
            )}
            <span className="filterTriggerChevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
        </ActionButton>
    );
}

export function FilterPanelBody(props) {
    const { isOpen, onClose } = props;

    const initialQuery = props.filters?.query || '';
    const initialQueryType = props.filters?.queryType || 'commander';
    const initialHigh = props.filters?.range?.high ?? 10;
    const initialLow = props.filters?.range?.low ?? 1;
    const initialColors = props?.filters?.colorFilters || null;

    const [query, setQuery] = useState(initialQuery);
    const [queryType, setQueryType] = useState(initialQueryType);
    const [high, setHigh] = useState(initialHigh);
    const [low, setLow] = useState(initialLow);
    const [colorFilters, setColorFilters] = useState(initialColors);

    useEffect(() => {
        if (isOpen) {
            setQuery(props.filters?.query || '');
            setQueryType(props.filters?.queryType || 'commander');
            setHigh(props.filters?.range?.high ?? 10);
            setLow(props.filters?.range?.low ?? 1);
            setColorFilters(props?.filters?.colorFilters || null);
        }
    }, [isOpen, props.filters]);

    const pushResetFilters = () => {
        setColorFilters(null);
        setLow(DEFAULT_FILTERS.range.low);
        setHigh(DEFAULT_FILTERS.range.high);
        setQuery('');
        setQueryType('commander');
        props?.onFiltersChange && props.onFiltersChange(DEFAULT_FILTERS);
    };

    const pushFiltersChangeUpstream = () => {
        let type = queryType;
        if (props?.restrictToCommander) type = 'commander';
        if (props?.restrictToAuthor) type = 'author';

        props?.onFiltersChange &&
            props.onFiltersChange({
                query,
                queryType: type,
                colorFilters,
                range: { high, low },
            });
    };

    const handleRangeSliderChange = (evn) => {
        setHigh(evn.end);
        setLow(evn.start);
    };

    const applyPowerPreset = (preset) => {
        setLow(preset.low);
        setHigh(preset.high);
    };

    const handleColorFiltersChange = (evn) => setColorFilters(evn.colors);
    const setAllColors = () => setColorFilters('wubrg');
    const clearColors = () => setColorFilters(null);

    const colorCount = colorFilters ? colorFilters.replace('c', '').length : 0;
    const isColorless = colorFilters === 'c';
    const rangeIsDefault = low === 1 && high === 10;

    const sideMargin = props?.transparentContainer ? 0 : 10;
    return (
        <div
            className={`searedInlineFilter ${isOpen ? 'is-open' : ''}`}
            aria-hidden={!isOpen}
            style={{ marginLeft: sideMargin, marginRight: sideMargin }}
        >
            <div className="searedInlineFilter__inner">
                <div className="searedFilterSections">
                    <View
                        isHidden={props?.hideQueryInput}
                        UNSAFE_className="searedFilterSection searedFilterSection--search"
                    >
                        <Text UNSAFE_className="filtersSectionLabel">Search</Text>
                        <SearchField
                            width="100%"
                            onChange={setQuery}
                            isDisabled={props?.hideQueryInput}
                            onClear={() => setQueryType('commander')}
                            value={query}
                            placeholder={`Search by ${queryType}…`}
                            marginTop={6}
                            marginBottom={10}
                        />
                        <div className="searedSegmented" role="tablist" aria-label="Search field">
                            {QUERY_TYPES.map((qt) => {
                                const disabled =
                                    props?.hideQueryInput ||
                                    (qt.value === 'author' &&
                                        (props?.restrictToCommander || props?.hideAuthorFilter)) ||
                                    (qt.value === 'title' && props?.restrictToCommander) ||
                                    (qt.value === 'commander' && props?.restrictToAuthor);
                                const active = queryType === qt.value;
                                return (
                                    <button
                                        key={qt.value}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        disabled={disabled}
                                        className={`searedSegment ${active ? 'is-active' : ''}`}
                                        onClick={() => setQueryType(qt.value)}
                                    >
                                        {qt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </View>

                    {!props?.disableColorSelector && (
                        <Flex direction="column" UNSAFE_className="searedFilterSection">
                            <Flex direction="row" justifyContent="space-between" alignItems="baseline">
                                <Text UNSAFE_className="filtersSectionLabel">Power level</Text>
                                <span className={`filterValueReadout ${rangeIsDefault ? 'is-muted' : ''}`}>
                                    {low} – {high}
                                </span>
                            </Flex>
                            <RangeSlider
                                UNSAFE_className="searedRangeSlider"
                                onChange={handleRangeSliderChange}
                                marginStart={0}
                                marginTop={6}
                                labelPosition="side"
                                label=" "
                                minValue={1}
                                maxValue={10}
                                value={{ start: low, end: high }}
                            />
                            <div className="searedChipRow">
                                {POWER_PRESETS.map((preset) => {
                                    const active = preset.low === low && preset.high === high;
                                    return (
                                        <button
                                            key={preset.label}
                                            type="button"
                                            className={`searedChip ${active ? 'is-active' : ''}`}
                                            onClick={() => applyPowerPreset(preset)}
                                        >
                                            {preset.label}
                                            <span className="searedChipRange">
                                                {preset.low}–{preset.high}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Flex>
                    )}

                    {!props?.disableColorSelector && (
                        <Flex direction="column" UNSAFE_className="searedFilterSection">
                            <Flex direction="row" justifyContent="space-between" alignItems="baseline">
                                <Text UNSAFE_className="filtersSectionLabel">Color identity</Text>
                                <span className={`filterValueReadout ${!colorFilters ? 'is-muted' : ''}`}>
                                    {isColorless ? 'Colorless' : colorCount === 0 ? 'Any' : `${colorCount} selected`}
                                </span>
                            </Flex>
                            <ManaSelector
                                handleColorFilterChange={handleColorFiltersChange}
                                colorFilters={colorFilters}
                            />
                            <div className="searedChipRow">
                                <button
                                    type="button"
                                    className="searedChip searedChip--ghost"
                                    onClick={setAllColors}
                                >
                                    All five
                                </button>
                                <button
                                    type="button"
                                    className="searedChip searedChip--ghost"
                                    onClick={clearColors}
                                    disabled={!colorFilters}
                                >
                                    Clear
                                </button>
                            </div>
                        </Flex>
                    )}
                </div>

                <div className="searedFilterFooter">
                        <button
                            type="button"
                            className="searedTextLink"
                            onClick={() => {
                                pushResetFilters();
                                onClose && onClose();
                            }}
                        >
                            Reset all
                        </button>
                        <Flex direction="row" gap="size-100">
                            <Button variant="secondary" onPress={() => onClose && onClose()}>
                                Cancel
                            </Button>
                            <Button
                                variant="accent"
                                onPress={() => {
                                    pushFiltersChangeUpstream();
                                    onClose && onClose();
                                }}
                            >
                                Apply
                            </Button>
                        </Flex>
                    </div>
            </div>
        </div>
    );
}
